import { repo } from '@/db/repo'
import { seedDatabase } from '@/db/seed'
import {
  ApiError,
  type Ministry,
  type Person,
  type Position,
  type ServiceType,
  type Team,
  type TeamMembership,
  type AppNotification,
  type ApiKey,
  type AppSettings,
  type Blockout,
  type MatrixCell,
  type MatrixSong,
  type MatrixView,
  type Plan,
  type PlanItem,
  type PlanWithDetails,
  type SchedulingAssignment,
  type Song,
  type Arrangement,
} from '@/types'
import { nowISO, sortBy, uid } from '@/lib/utils'
import { required, requireBody, type Route } from '@/services/api/router'

const ts = () => nowISO()

/** Cycled when adding people, so avatars stay visually distinguishable. */
const AVATAR_COLORS = [
  '#6366f1', '#ec4899', '#0ea5e9', '#f59e0b', '#22c55e', '#a855f7',
  '#ef4444', '#14b8a6', '#8b5cf6', '#f97316', '#3b82f6', '#d946ef',
]

/* -------------------------------------------------------------------------- */
/* Organisation                                                               */
/* -------------------------------------------------------------------------- */

const orgRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/ministries',
    visibility: 'admin',
    description: 'All ministries, ordered for the team tree.',
    handler: async () => sortBy(await repo.all('ministries'), (m) => m.sortOrder),
  },
  {
    method: 'GET',
    pattern: '/teams',
    visibility: 'admin',
    description: 'All teams. Filter with ?ministryId=',
    handler: async (ctx) => {
      const ministryId = ctx.query.get('ministryId')
      const teams = ministryId
        ? await repo.byIndex('teams', 'by-ministry', ministryId)
        : await repo.all('teams')
      return sortBy(teams, (t) => t.sortOrder)
    },
  },
  {
    method: 'POST',
    pattern: '/teams',
    visibility: 'admin',
    description: 'Creates a team inside a ministry.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Team>>(ctx.body)
      if (!body.name?.trim()) throw new ApiError(400, 'invalid_body', 'name is required')
      const ministries = await repo.all('ministries')
      const ministryId = body.ministryId ?? ministries[0]?.id
      if (!ministryId) throw new ApiError(409, 'no_ministries', 'Create a ministry first')
      const existing = await repo.all('teams')
      const now = ts()
      const team: Team = {
        id: uid('team'),
        ministryId,
        name: body.name.trim(),
        description: body.description,
        sortOrder: body.sortOrder ?? existing.length + 1,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('teams', team)
      return team
    },
  },
  {
    method: 'PATCH',
    pattern: '/teams/:teamId',
    visibility: 'admin',
    description: 'Renames or moves a team.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Team>>(ctx.body)
      return required(await repo.patch('teams', ctx.params.teamId, body), 'Team', ctx.params.teamId)
    },
  },
  {
    method: 'DELETE',
    pattern: '/teams/:teamId',
    visibility: 'admin',
    description: 'Deletes a team, its positions and memberships. Refuses if it is scheduled.',
    handler: async (ctx) => {
      const { teamId } = ctx.params
      required(await repo.get('teams', teamId), 'Team', teamId)
      const assignments = await repo.byIndex('assignments', 'by-team', teamId)
      if (assignments.length > 0) {
        throw new ApiError(
          409,
          'in_use',
          `${assignments.length} scheduled position(s) still reference this team.`,
        )
      }
      const positions = await repo.byIndex('positions', 'by-team', teamId)
      const memberships = await repo.byIndex('memberships', 'by-team', teamId)
      await Promise.all([
        ...positions.map((p) => repo.remove('positions', p.id)),
        ...memberships.map((m) => repo.remove('memberships', m.id)),
      ])
      await repo.remove('teams', teamId)
      return { id: teamId, deleted: true, positions: positions.length }
    },
  },
  {
    method: 'POST',
    pattern: '/positions',
    visibility: 'admin',
    description: 'Creates a position on a team (Bass Guitar, FOH Engineer…).',
    handler: async (ctx) => {
      const body = requireBody<Partial<Position>>(ctx.body)
      if (!body.name?.trim()) throw new ApiError(400, 'invalid_body', 'name is required')
      if (!body.teamId) throw new ApiError(400, 'invalid_body', 'teamId is required')
      required(await repo.get('teams', body.teamId), 'Team', body.teamId)
      const existing = await repo.all('positions')
      const now = ts()
      const position: Position = {
        id: uid('pos'),
        teamId: body.teamId,
        name: body.name.trim(),
        defaultQuantity: Math.max(1, body.defaultQuantity ?? 1),
        sortOrder: body.sortOrder ?? existing.length + 1,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('positions', position)
      return position
    },
  },
  {
    method: 'PATCH',
    pattern: '/positions/:positionId',
    visibility: 'admin',
    description: 'Renames a position or changes how many are needed.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Position>>(ctx.body)
      return required(
        await repo.patch('positions', ctx.params.positionId, body),
        'Position',
        ctx.params.positionId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/positions/:positionId',
    visibility: 'admin',
    description: 'Deletes a position. Refuses while it is scheduled on a plan.',
    handler: async (ctx) => {
      const { positionId } = ctx.params
      required(await repo.get('positions', positionId), 'Position', positionId)
      const assignments = (await repo.all('assignments')).filter((a) => a.positionId === positionId)
      if (assignments.length > 0) {
        throw new ApiError(409, 'in_use', `${assignments.length} plan(s) still schedule this position.`)
      }
      // Drop it from anyone's qualifications as well.
      const memberships = await repo.all('memberships')
      await Promise.all(
        memberships
          .filter((m) => m.positionIds.includes(positionId))
          .map((m) =>
            repo.patch('memberships', m.id, {
              positionIds: m.positionIds.filter((id) => id !== positionId),
            }),
          ),
      )
      await repo.remove('positions', positionId)
      return { id: positionId, deleted: true }
    },
  },
  {
    method: 'POST',
    pattern: '/memberships',
    visibility: 'admin',
    description: 'Adds a person to a team, with the positions they can fill.',
    handler: async (ctx) => {
      const body = requireBody<Partial<TeamMembership>>(ctx.body)
      if (!body.personId || !body.teamId) {
        throw new ApiError(400, 'invalid_body', 'personId and teamId are required')
      }
      const existing = (await repo.byIndex('memberships', 'by-team', body.teamId)).find(
        (m) => m.personId === body.personId,
      )
      const now = ts()
      // One membership per person per team — adding twice updates instead.
      const membership: TeamMembership = {
        id: existing?.id ?? uid('mem'),
        personId: body.personId,
        teamId: body.teamId,
        positionIds: body.positionIds ?? existing?.positionIds ?? [],
        proficiency: body.proficiency ?? existing?.proficiency ?? 'qualified',
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      }
      await repo.put('memberships', membership)

      // Person.teamIds is denormalised for the matrix — keep it in step.
      const person = await repo.get('people', body.personId)
      if (person && !person.teamIds.includes(body.teamId)) {
        await repo.patch('people', person.id, { teamIds: [...person.teamIds, body.teamId] })
      }
      return membership
    },
  },
  {
    method: 'PATCH',
    pattern: '/memberships/:membershipId',
    visibility: 'admin',
    description: 'Updates which positions a member covers, or their proficiency.',
    handler: async (ctx) => {
      const body = requireBody<Partial<TeamMembership>>(ctx.body)
      return required(
        await repo.patch('memberships', ctx.params.membershipId, body),
        'Membership',
        ctx.params.membershipId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/memberships/:membershipId',
    visibility: 'admin',
    description: 'Removes a person from a team.',
    handler: async (ctx) => {
      const membership = required(
        await repo.get('memberships', ctx.params.membershipId),
        'Membership',
        ctx.params.membershipId,
      )
      await repo.remove('memberships', membership.id)
      const person = await repo.get('people', membership.personId)
      const stillOn = (await repo.byIndex('memberships', 'by-person', membership.personId)).some(
        (m) => m.teamId === membership.teamId,
      )
      if (person && !stillOn) {
        await repo.patch('people', person.id, {
          teamIds: person.teamIds.filter((id) => id !== membership.teamId),
        })
      }
      return { id: membership.id, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/positions',
    visibility: 'admin',
    description: 'All positions. Filter with ?teamId=',
    handler: async (ctx) => {
      const teamId = ctx.query.get('teamId')
      const positions = teamId
        ? await repo.byIndex('positions', 'by-team', teamId)
        : await repo.all('positions')
      return sortBy(positions, (p) => p.sortOrder)
    },
  },
  {
    method: 'POST',
    pattern: '/ministries',
    visibility: 'admin',
    description: 'Creates a ministry — the folder service types and teams live in.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Ministry>>(ctx.body)
      if (!body.name?.trim()) throw new ApiError(400, 'invalid_body', 'name is required')
      const existing = await repo.all('ministries')
      const now = ts()
      const ministry: Ministry = {
        id: uid('min'),
        name: body.name.trim(),
        description: body.description,
        color: body.color || '#6366f1',
        sortOrder: body.sortOrder ?? existing.length + 1,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('ministries', ministry)
      return ministry
    },
  },
  {
    method: 'PATCH',
    pattern: '/ministries/:ministryId',
    visibility: 'admin',
    description: 'Renames or recolours a ministry.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Ministry>>(ctx.body)
      return required(
        await repo.patch('ministries', ctx.params.ministryId, body),
        'Ministry',
        ctx.params.ministryId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/ministries/:ministryId',
    visibility: 'admin',
    description: 'Deletes an empty ministry. Refuses while teams or service types remain.',
    handler: async (ctx) => {
      const { ministryId } = ctx.params
      required(await repo.get('ministries', ministryId), 'Ministry', ministryId)
      const [teams, serviceTypes] = await Promise.all([
        repo.byIndex('teams', 'by-ministry', ministryId),
        repo.all('serviceTypes'),
      ])
      const usedBy = serviceTypes.filter((s) => s.ministryId === ministryId)
      if (teams.length > 0 || usedBy.length > 0) {
        throw new ApiError(
          409,
          'not_empty',
          `Move or delete its ${teams.length} team(s) and ${usedBy.length} service type(s) first.`,
        )
      }
      await repo.remove('ministries', ministryId)
      return { id: ministryId, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/service-types',
    visibility: 'admin',
    description: 'Service types that drive the scheduling matrix.',
    handler: async () => sortBy(await repo.all('serviceTypes'), (s) => s.sortOrder),
  },
  {
    method: 'POST',
    pattern: '/service-types',
    visibility: 'admin',
    description: 'Creates a service type (Sunday 9:30, Midweek Prayer…).',
    handler: async (ctx) => {
      const body = requireBody<Partial<ServiceType>>(ctx.body)
      if (!body.name?.trim()) throw new ApiError(400, 'invalid_body', 'name is required')
      const ministries = await repo.all('ministries')
      const ministryId = body.ministryId ?? ministries[0]?.id
      if (!ministryId) throw new ApiError(409, 'no_ministries', 'Create a ministry first')
      const existing = await repo.all('serviceTypes')
      const now = ts()
      const serviceType: ServiceType = {
        id: uid('stype'),
        name: body.name.trim(),
        ministryId,
        teamIds: body.teamIds ?? [],
        defaultStartTime: body.defaultStartTime || '09:30',
        autoConfirm: body.autoConfirm ?? false,
        sortOrder: body.sortOrder ?? existing.length + 1,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('serviceTypes', serviceType)
      return serviceType
    },
  },
  {
    method: 'PATCH',
    pattern: '/service-types/:serviceTypeId',
    visibility: 'admin',
    description: 'Updates a service type — name, group, default time or teams.',
    handler: async (ctx) => {
      const body = requireBody<Partial<ServiceType>>(ctx.body)
      return required(
        await repo.patch('serviceTypes', ctx.params.serviceTypeId, body),
        'Service type',
        ctx.params.serviceTypeId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/service-types/:serviceTypeId',
    visibility: 'admin',
    description: 'Deletes a service type. Refuses while plans still reference it.',
    handler: async (ctx) => {
      const { serviceTypeId } = ctx.params
      required(await repo.get('serviceTypes', serviceTypeId), 'Service type', serviceTypeId)
      const plans = await repo.byIndex('plans', 'by-serviceType', serviceTypeId)
      if (plans.length > 0) {
        throw new ApiError(
          409,
          'in_use',
          `${plans.length} plan(s) still use this service type. Delete them first.`,
        )
      }
      await repo.remove('serviceTypes', serviceTypeId)
      return { id: serviceTypeId, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/directory',
    visibility: 'admin',
    description: "Searches MyMDV Hub's church directory, for filling in the add-person form.",
    handler: async (ctx) => {
      /*
       * The demo has no Hub to read, so this stands in for it — a handful of
       * names that are NOT already in the Services directory, plus one that is,
       * so the "Already here" state is visible without contriving it.
       *
       * Against the real API this is a cross-database read of Hub's `people`
       * table; see api/lib/Directory.php.
       */
      const q = ctx.query.get('q')?.toLowerCase().trim() ?? ''
      const existing = await repo.all('people')
      const invented = [
        { firstName: 'Marisol', lastName: 'Vega', email: 'marisol.vega@example.org', phone: '(305) 555-0142' },
        { firstName: 'Elián', lastName: 'Cruz', email: 'elian.cruz@example.org', phone: null },
        { firstName: 'Noemí', lastName: 'Duarte', email: 'noemi.duarte@example.org', phone: '(305) 555-0188' },
        { firstName: 'Abel', lastName: 'Ramos', email: 'abel.ramos@example.org', phone: null },
      ]
      const pool = [
        ...invented,
        ...existing.slice(0, 2).map((p) => ({
          firstName: p.firstName, lastName: p.lastName, email: p.email, phone: p.phone ?? null,
        })),
      ]
      const taken = new Set(existing.map((p) => p.email.toLowerCase()))
      return {
        available: true,
        people: pool
          .filter((p) => !q || `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(q))
          .map((p) => ({ ...p, alreadyAdded: taken.has(p.email.toLowerCase()) })),
      }
    },
  },
  {
    method: 'GET',
    pattern: '/people',
    visibility: 'admin',
    description: 'Directory of volunteers. Filter with ?teamId= or ?q=',
    handler: async (ctx) => {
      const teamId = ctx.query.get('teamId')
      const q = ctx.query.get('q')?.toLowerCase().trim()
      let people = await repo.all('people')
      if (teamId) people = people.filter((p) => p.teamIds.includes(teamId))
      if (q) {
        people = people.filter((p) =>
          `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(q),
        )
      }
      return sortBy(people, (p) => `${p.lastName} ${p.firstName}`)
    },
  },
  {
    method: 'POST',
    pattern: '/people',
    visibility: 'admin',
    description: 'Adds someone to the directory.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Person>>(ctx.body)
      const firstName = body.firstName?.trim()
      const email = body.email?.trim().toLowerCase()
      if (!firstName) throw new ApiError(400, 'invalid_body', 'firstName is required')
      if (!email) throw new ApiError(400, 'invalid_body', 'email is required')

      const existing = await repo.all('people')
      if (existing.some((p) => p.email.toLowerCase() === email)) {
        throw new ApiError(409, 'email_taken', `Somebody already uses ${email}.`)
      }

      const now = ts()
      const person: Person = {
        id: uid('person'),
        firstName,
        lastName: body.lastName?.trim() ?? '',
        email,
        phone: body.phone,
        role: body.role ?? 'member',
        // Cycle the palette so a new directory does not come out all one colour.
        avatarColor: body.avatarColor ?? AVATAR_COLORS[existing.length % AVATAR_COLORS.length],
        teamIds: [],
        notes: body.notes,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('people', person)
      return person
    },
  },
  {
    method: 'PATCH',
    pattern: '/people/:personId',
    visibility: 'admin',
    description: 'Updates someone in the directory.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Person>>(ctx.body)
      if (body.email) {
        const email = body.email.trim().toLowerCase()
        const clash = (await repo.all('people')).find(
          (p) => p.email.toLowerCase() === email && p.id !== ctx.params.personId,
        )
        if (clash) throw new ApiError(409, 'email_taken', `Somebody already uses ${email}.`)
        body.email = email
      }
      return required(
        await repo.patch('people', ctx.params.personId, body),
        'Person',
        ctx.params.personId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/people/:personId',
    visibility: 'admin',
    description: 'Removes someone. Their scheduled slots are left unfilled, not deleted.',
    handler: async (ctx) => {
      const { personId } = ctx.params
      required(await repo.get('people', personId), 'Person', personId)

      const settings = await repo.get('settings', 'app-settings')
      if (settings?.currentPersonId === personId) {
        throw new ApiError(409, 'is_current_user', 'You cannot remove the account you are signed in as.')
      }

      // Empty their slots rather than deleting the position from the plan.
      const assignments = await repo.byIndex('assignments', 'by-person', personId)
      await Promise.all(
        assignments.map((a) =>
          repo.patch('assignments', a.id, { personId: null, status: 'unscheduled' }),
        ),
      )
      await repo.removeByIndex('memberships', 'by-person', personId)
      await repo.removeByIndex('blockouts', 'by-person', personId)
      await repo.remove('people', personId)
      return { id: personId, deleted: true, slotsCleared: assignments.length }
    },
  },
  {
    method: 'GET',
    pattern: '/memberships',
    visibility: 'admin',
    description: 'Which people can fill which positions. Filter with ?teamId= / ?personId=',
    handler: async (ctx) => {
      const teamId = ctx.query.get('teamId')
      const personId = ctx.query.get('personId')
      if (teamId) return repo.byIndex('memberships', 'by-team', teamId)
      if (personId) return repo.byIndex('memberships', 'by-person', personId)
      return repo.all('memberships')
    },
  },
]

/* -------------------------------------------------------------------------- */
/* Plans and the order of service                                             */
/* -------------------------------------------------------------------------- */

/**
 * Does a blockout cover this date?
 *
 * Annual blockouts compare month and day only, and must handle a range that
 * straddles new year (27 Dec – 2 Jan) — which is precisely when people are
 * away, and precisely what a naive comparison gets wrong.
 */
function blockoutCovers(blockout: Blockout, date: string): boolean {
  if (!blockout.repeatsAnnually) {
    return date >= blockout.startDate && date <= blockout.endDate
  }
  const md = (value: string) => value.slice(5)
  const [day, from, to] = [md(date), md(blockout.startDate), md(blockout.endDate)]
  return from <= to ? day >= from && day <= to : day >= from || day <= to
}

/**
 * Lay a plan's rota out from a set of teams.
 *
 * One unfilled slot per position per `defaultQuantity`, so a position that
 * needs two singers gets two rows to fill. Nothing here picks people: the slot
 * is the question, `personId` is the answer, and a plan with no slots is a
 * rota nobody can be added to.
 *
 * Counts what the plan already holds for each position, so running it twice
 * adds nothing. Mirrors `fill_plan_from_teams()` in api/routes/admin.php.
 */
async function fillPlanFromTeams(planId: string, teamIds: string[]): Promise<SchedulingAssignment[]> {
  if (teamIds.length === 0) return []
  const [allPositions, existing] = await Promise.all([
    repo.all('positions'),
    repo.byIndex('assignments', 'by-plan', planId),
  ])
  const positions = sortBy(
    allPositions.filter((position) => teamIds.includes(position.teamId)),
    (position) => position.sortOrder,
  )

  const now = ts()
  const created: SchedulingAssignment[] = []
  for (const position of positions) {
    const have = existing.filter((a) => a.positionId === position.id).length
    const want = Math.max(1, position.defaultQuantity)
    for (let i = have; i < want; i += 1) {
      created.push({
        id: uid('asg'),
        planId,
        positionId: position.id,
        teamId: position.teamId,
        personId: null,
        status: 'unscheduled',
        createdAt: now,
        updatedAt: now,
      })
    }
  }
  if (created.length > 0) await repo.putMany('assignments', created)
  return created
}

/** The team ids a service type normally schedules. */
async function serviceTypeTeamIds(serviceTypeId: string): Promise<string[]> {
  const serviceType = await repo.get('serviceTypes', serviceTypeId)
  return serviceType?.teamIds ?? []
}

async function planWithDetails(planId: string): Promise<PlanWithDetails> {
  const plan = required(await repo.get('plans', planId), 'Plan', planId)
  const [items, assignments, serviceTypes, blockouts] = await Promise.all([
    repo.byIndex('planItems', 'by-plan', planId),
    repo.byIndex('assignments', 'by-plan', planId),
    repo.all('serviceTypes'),
    repo.all('blockouts'),
  ])
  const sortedItems = sortBy(items, (i) => i.sortOrder)
  return {
    ...plan,
    serviceType: serviceTypes.find((s) => s.id === plan.serviceTypeId),
    items: sortedItems,
    assignments,
    totalDurationSeconds: sortedItems.reduce((sum, i) => sum + i.durationSeconds, 0),
    blockouts: blockouts
      .filter((blockout) => blockoutCovers(blockout, plan.serviceDate))
      .map((blockout) => ({
        personId: blockout.personId,
        reason: blockout.reason ?? null,
        startDate: blockout.startDate,
        endDate: blockout.endDate,
        annual: blockout.repeatsAnnually,
      })),
  }
}

/** Rewrites `sortOrder` to 1..n so the timeline never has gaps. */
async function normaliseOrder(planId: string): Promise<PlanItem[]> {
  const items = sortBy(await repo.byIndex('planItems', 'by-plan', planId), (i) => i.sortOrder)
  const renumbered = items.map((item, index) => ({ ...item, sortOrder: index + 1 }))
  await repo.putMany('planItems', renumbered)
  return renumbered
}

const planRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/plans',
    visibility: 'admin',
    description: 'Service plans. Filter with ?from=, ?to=, ?serviceTypeId=, ?status=',
    handler: async (ctx) => {
      const from = ctx.query.get('from')
      const to = ctx.query.get('to')
      const serviceTypeId = ctx.query.get('serviceTypeId')
      const status = ctx.query.get('status')
      let plans = await repo.all('plans')
      if (from) plans = plans.filter((p) => p.serviceDate >= from)
      if (to) plans = plans.filter((p) => p.serviceDate <= to)
      if (serviceTypeId) plans = plans.filter((p) => p.serviceTypeId === serviceTypeId)
      if (status) plans = plans.filter((p) => p.status === status)
      return sortBy(plans, (p) => `${p.serviceDate} ${p.startTime}`)
    },
  },
  {
    method: 'GET',
    pattern: '/plans/:planId',
    visibility: 'admin',
    description: 'One plan, joined with its items and assignments.',
    handler: async (ctx) => planWithDetails(ctx.params.planId),
  },
  {
    method: 'POST',
    pattern: '/plans',
    visibility: 'admin',
    description: 'Creates a service plan.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Plan>>(ctx.body)
      if (!body.serviceDate) throw new ApiError(400, 'invalid_body', 'serviceDate is required')
      const serviceTypes = await repo.all('serviceTypes')
      const serviceType =
        serviceTypes.find((s) => s.id === body.serviceTypeId) ?? serviceTypes[0]
      if (!serviceType) throw new ApiError(409, 'no_service_types', 'Create a service type first')
      const now = ts()
      const plan: Plan = {
        id: uid('plan'),
        serviceTypeId: serviceType.id,
        title: body.title || serviceType.name,
        serviceDate: body.serviceDate,
        startTime: body.startTime || serviceType.defaultStartTime,
        status: body.status || 'published',
        seriesTitle: body.seriesTitle,
        seriesArtworkColor: body.seriesArtworkColor,
        notes: body.notes,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('plans', plan)
      // A plan with no slots is a rota nobody can be added to.
      await fillPlanFromTeams(plan.id, serviceType.teamIds ?? [])
      return plan
    },
  },
  {
    method: 'POST',
    pattern: '/plans/bulk',
    visibility: 'admin',
    description:
      'Creates a run of plans on a repeating cadence. Body: { serviceTypeId, count, startDate, intervalDays, templatePlanId?, copyItems?, copyTeams? }',
    handler: async (ctx) => {
      const body = requireBody<{
        serviceTypeId?: string
        count?: number
        startDate?: string
        intervalDays?: number
        templatePlanId?: string | null
        copyItems?: boolean
        copyTeams?: boolean
        status?: Plan['status']
        titlePattern?: string
      }>(ctx.body)

      const count = Math.min(Math.max(1, Number(body.count ?? 1)), 52)
      const intervalDays = Math.min(Math.max(1, Number(body.intervalDays ?? 7)), 90)
      if (!body.startDate) throw new ApiError(400, 'invalid_body', 'startDate is required')

      const serviceTypes = await repo.all('serviceTypes')
      const serviceType = serviceTypes.find((s) => s.id === body.serviceTypeId)
      if (!serviceType) throw new ApiError(400, 'invalid_body', 'A known serviceTypeId is required')

      // The template supplies the start time and, optionally, the running order.
      const template = body.templatePlanId
        ? await repo.get('plans', body.templatePlanId)
        : undefined
      const templateItems =
        template && body.copyItems !== false
          ? sortBy(await repo.byIndex('planItems', 'by-plan', template.id), (i) => i.sortOrder)
          : []
      const templateAssignments =
        template && body.copyTeams
          ? await repo.byIndex('assignments', 'by-plan', template.id)
          : []

      const [year, month, day] = body.startDate.split('-').map(Number)
      const created: Plan[] = []
      const items: PlanItem[] = []
      const assignments: SchedulingAssignment[] = []
      const now = ts()

      for (let index = 0; index < count; index += 1) {
        // Built from a local Date so daylight-saving shifts cannot slide a date.
        const date = new Date(year, (month ?? 1) - 1, (day ?? 1) + index * intervalDays)
        const serviceDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
          date.getDate(),
        ).padStart(2, '0')}`

        const planId = uid('plan')
        created.push({
          id: planId,
          serviceTypeId: serviceType.id,
          title: (body.titlePattern || serviceType.name).replace('{date}', serviceDate),
          serviceDate,
          startTime: template?.startTime || serviceType.defaultStartTime,
          status: body.status ?? 'published',
          seriesTitle: template?.seriesTitle,
          seriesArtworkColor: template?.seriesArtworkColor,
          createdAt: now,
          updatedAt: now,
        })

        templateItems.forEach((item, itemIndex) => {
          items.push({
            ...item,
            id: uid('item'),
            planId,
            sortOrder: itemIndex + 1,
            createdAt: now,
            updatedAt: now,
          })
        })

        // Copied rotas start empty: the slots exist, nobody is committed yet.
        templateAssignments.forEach((assignment) => {
          assignments.push({
            ...assignment,
            id: uid('asg'),
            planId,
            personId: null,
            status: 'unscheduled',
            respondedAt: undefined,
            declineReason: undefined,
            createdAt: now,
            updatedAt: now,
          })
        })
      }

      await Promise.all([
        repo.putMany('plans', created),
        repo.putMany('planItems', items),
        repo.putMany('assignments', assignments),
      ])

      /*
       * No template means nothing to copy, so lay each rota out from the
       * service type. With a template the caller has already said what they
       * want copied — honour that and add nothing.
       */
      if (!template) {
        for (const plan of created) {
          await fillPlanFromTeams(plan.id, serviceType.teamIds ?? [])
        }
      }

      return { created: created.length, plans: created }
    },
  },
  {
    method: 'POST',
    pattern: '/plans/:planId/teams',
    visibility: 'admin',
    description:
      "Lays this plan's rota out from its service type's teams. Body: { teamIds?: string[] }. Adds only what is missing.",
    handler: async (ctx) => {
      const planId = ctx.params.planId ?? ''
      const plan = required(await repo.get('plans', planId), 'Plan', planId)
      const body = (ctx.body ?? {}) as { teamIds?: string[] }
      const teamIds =
        Array.isArray(body.teamIds) && body.teamIds.length > 0
          ? body.teamIds
          : await serviceTypeTeamIds(plan.serviceTypeId)
      if (teamIds.length === 0) {
        throw new ApiError(
          409,
          'no_teams',
          'This service type has no teams yet. Add them under Service Plans, then try again.',
        )
      }
      const created = await fillPlanFromTeams(plan.id, teamIds)
      return {
        created: created.length,
        assignments: await repo.byIndex('assignments', 'by-plan', plan.id),
      }
    },
  },
  {
    method: 'PATCH',
    pattern: '/plans/:planId',
    visibility: 'admin',
    description: 'Updates plan metadata (title, date, status, notes).',
    handler: async (ctx) => {
      const body = requireBody<Partial<Plan>>(ctx.body)
      return required(await repo.patch('plans', ctx.params.planId, body), 'Plan', ctx.params.planId)
    },
  },
  {
    method: 'DELETE',
    pattern: '/plans/:planId',
    visibility: 'admin',
    description: 'Deletes a plan and cascades to its items and assignments.',
    handler: async (ctx) => {
      const { planId } = ctx.params
      required(await repo.get('plans', planId), 'Plan', planId)
      const [items, assignments] = await Promise.all([
        repo.removeByIndex('planItems', 'by-plan', planId),
        repo.removeByIndex('assignments', 'by-plan', planId),
      ])
      await repo.remove('plans', planId)
      return { id: planId, deleted: true, items, assignments }
    },
  },
  {
    method: 'GET',
    pattern: '/plans/:planId/items',
    visibility: 'admin',
    description: 'Order of service for a plan, in running order.',
    handler: async (ctx) =>
      sortBy(await repo.byIndex('planItems', 'by-plan', ctx.params.planId), (i) => i.sortOrder),
  },
  {
    method: 'POST',
    pattern: '/plans/:planId/items',
    visibility: 'admin',
    description: 'Appends an item to the order of service.',
    handler: async (ctx) => {
      const { planId } = ctx.params
      required(await repo.get('plans', planId), 'Plan', planId)
      const body = requireBody<Partial<PlanItem>>(ctx.body)
      const existing = await repo.byIndex('planItems', 'by-plan', planId)
      const now = ts()
      const item: PlanItem = {
        id: uid('item'),
        planId,
        type: body.type || 'custom',
        title: body.title?.trim() || 'Untitled item',
        description: body.description,
        durationSeconds: Math.max(0, body.durationSeconds ?? 300),
        sortOrder:
          body.sortOrder ?? existing.reduce((max, i) => Math.max(max, i.sortOrder), 0) + 1,
        songId: body.songId,
        arrangementId: body.arrangementId,
        keyOverride: body.keyOverride,
        responsiblePersonId: body.responsiblePersonId,
        techNotes: body.techNotes,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('planItems', item)
      await repo.patch('plans', planId, { updatedAt: now })

      // Keep the song library's usage stats honest.
      if (item.songId) {
        const song = await repo.get('songs', item.songId)
        const plan = await repo.get('plans', planId)
        if (song && plan) {
          await repo.patch('songs', song.id, {
            timesScheduled: song.timesScheduled + 1,
            lastScheduledDate:
              !song.lastScheduledDate || plan.serviceDate > song.lastScheduledDate
                ? plan.serviceDate
                : song.lastScheduledDate,
          })
        }
      }
      return item
    },
  },
  {
    method: 'PATCH',
    pattern: '/plan-items/:itemId',
    visibility: 'admin',
    description: 'Updates one item — title, duration, tech notes, arrangement.',
    handler: async (ctx) => {
      const body = requireBody<Partial<PlanItem>>(ctx.body)
      const updated = required(
        await repo.patch('planItems', ctx.params.itemId, body),
        'Plan item',
        ctx.params.itemId,
      )
      await repo.patch('plans', updated.planId, { updatedAt: ts() })
      return updated
    },
  },
  {
    method: 'DELETE',
    pattern: '/plan-items/:itemId',
    visibility: 'admin',
    description: 'Removes an item and closes the gap in the running order.',
    handler: async (ctx) => {
      const item = required(
        await repo.get('planItems', ctx.params.itemId),
        'Plan item',
        ctx.params.itemId,
      )
      await repo.remove('planItems', item.id)
      await normaliseOrder(item.planId)
      return { id: item.id, deleted: true }
    },
  },
  {
    method: 'POST',
    pattern: '/plans/:planId/items/reorder',
    visibility: 'admin',
    description: 'Persists a drag-and-drop reorder. Body: { itemIds: string[] }',
    handler: async (ctx) => {
      const { planId } = ctx.params
      const body = requireBody<{ itemIds?: unknown }>(ctx.body)
      const itemIds = body.itemIds
      if (!Array.isArray(itemIds) || itemIds.some((id) => typeof id !== 'string')) {
        throw new ApiError(400, 'invalid_body', 'itemIds must be an array of ids')
      }
      const items = await repo.byIndex('planItems', 'by-plan', planId)
      const byId = new Map(items.map((i) => [i.id, i]))
      const now = ts()
      const reordered = (itemIds as string[])
        .map((id, index) => {
          const item = byId.get(id)
          return item ? { ...item, sortOrder: index + 1, updatedAt: now } : null
        })
        .filter((i): i is PlanItem => i !== null)

      // Anything the client did not mention keeps its relative position at the end.
      const missing = items
        .filter((i) => !itemIds.includes(i.id))
        .map((i, index) => ({ ...i, sortOrder: reordered.length + index + 1, updatedAt: now }))

      await repo.putMany('planItems', [...reordered, ...missing])
      await repo.patch('plans', planId, { updatedAt: now })
      return sortBy([...reordered, ...missing], (i) => i.sortOrder)
    },
  },
]

/* -------------------------------------------------------------------------- */
/* Scheduling                                                                 */
/* -------------------------------------------------------------------------- */

const scheduleRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/assignments',
    visibility: 'admin',
    description: 'Assignments. Filter with ?planId=, ?personId=, ?teamId=, ?status=',
    handler: async (ctx) => {
      const planId = ctx.query.get('planId')
      const personId = ctx.query.get('personId')
      const teamId = ctx.query.get('teamId')
      const status = ctx.query.get('status')
      let assignments: SchedulingAssignment[]
      if (planId) assignments = await repo.byIndex('assignments', 'by-plan', planId)
      else if (personId) assignments = await repo.byIndex('assignments', 'by-person', personId)
      else if (teamId) assignments = await repo.byIndex('assignments', 'by-team', teamId)
      else assignments = await repo.all('assignments')
      return status ? assignments.filter((a) => a.status === status) : assignments
    },
  },
  {
    method: 'POST',
    pattern: '/assignments',
    visibility: 'admin',
    description: 'Schedules a volunteer into a position on a plan.',
    handler: async (ctx) => {
      const body = requireBody<Partial<SchedulingAssignment>>(ctx.body)
      if (!body.planId || !body.positionId) {
        throw new ApiError(400, 'invalid_body', 'planId and positionId are required')
      }
      const position = required(await repo.get('positions', body.positionId), 'Position')
      const now = ts()
      /* Auto-confirm applies here too, or a slot created with somebody already
         in it arrives pending on a rota that schedules outright. Mirrors
         api/routes/admin.php. */
      const plan = await repo.get('plans', body.planId)
      const serviceType = plan ? await repo.get('serviceTypes', plan.serviceTypeId) : undefined
      const auto = Boolean(serviceType?.autoConfirm)
      const assignment: SchedulingAssignment = {
        id: uid('asg'),
        planId: body.planId,
        positionId: position.id,
        teamId: position.teamId,
        personId: body.personId ?? null,
        status: body.personId ? (body.status ?? (auto ? 'confirmed' : 'pending')) : 'unscheduled',
        respondedAt: body.personId && auto ? now : undefined,
        notes: body.notes,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('assignments', assignment)
      return assignment
    },
  },
  {
    method: 'PATCH',
    pattern: '/assignments/:assignmentId',
    visibility: 'admin',
    description: 'Confirms, declines or reassigns a position.',
    handler: async (ctx) => {
      const body = requireBody<Partial<SchedulingAssignment>>(ctx.body)
      const patch: Partial<SchedulingAssignment> = { ...body }
      if (body.status === 'confirmed' || body.status === 'declined') {
        patch.respondedAt = ts()
      }
      if (body.personId === null) {
        patch.status = 'unscheduled'
      } else if (body.personId !== undefined && body.status === undefined) {
        /*
         * Filling a slot: the server decides what that means, because only it
         * knows whether the service type schedules people outright. Mirrors
         * the same branch in api/routes/admin.php — a difference between the
         * two is invisible until production.
         */
        const existing = await repo.get('assignments', ctx.params.assignmentId)
        const plan = existing ? await repo.get('plans', existing.planId) : undefined
        const serviceType = plan ? await repo.get('serviceTypes', plan.serviceTypeId) : undefined
        const auto = Boolean(serviceType?.autoConfirm)
        patch.status = auto ? 'confirmed' : 'pending'
        patch.respondedAt = auto ? ts() : undefined
      }
      return required(
        await repo.patch('assignments', ctx.params.assignmentId, patch),
        'Assignment',
        ctx.params.assignmentId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/assignments/:assignmentId',
    visibility: 'admin',
    description: 'Removes an assignment slot entirely.',
    handler: async (ctx) => {
      required(await repo.get('assignments', ctx.params.assignmentId), 'Assignment')
      await repo.remove('assignments', ctx.params.assignmentId)
      return { id: ctx.params.assignmentId, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/matrix',
    visibility: 'admin',
    description: 'Dates x teams grid for the scheduling dashboard. ?from=&to=',
    handler: async (ctx): Promise<MatrixView> => {
      const from = ctx.query.get('from')
      const to = ctx.query.get('to')
      const [allPlans, teams, allAssignments, allItems, allSongs, allArrangements] = await Promise.all([
        repo.all('plans'),
        repo.all('teams'),
        repo.all('assignments'),
        repo.all('planItems'),
        repo.all('songs'),
        repo.all('arrangements'),
      ])
      const plans = sortBy(
        allPlans.filter((p) => (!from || p.serviceDate >= from) && (!to || p.serviceDate <= to)),
        (p) => `${p.serviceDate} ${p.startTime}`,
      )
      const planIds = new Set(plans.map((p) => p.id))
      const cells: Record<string, MatrixCell> = {}

      for (const assignment of allAssignments) {
        if (!planIds.has(assignment.planId)) continue
        const key = `${assignment.planId}:${assignment.teamId}`
        const cell = (cells[key] ||= {
          planId: assignment.planId,
          teamId: assignment.teamId,
          confirmed: 0,
          pending: 0,
          declined: 0,
          unfilled: 0,
          people: [],
          open: {},
        })
        if (assignment.status === 'confirmed') cell.confirmed += 1
        else if (assignment.status === 'pending') cell.pending += 1
        else if (assignment.status === 'declined') cell.declined += 1
        else {
          cell.unfilled += 1
          // Per position, mirroring routes/admin.php — see the note there.
          cell.open[assignment.positionId] = (cell.open[assignment.positionId] ?? 0) + 1
        }
        if (assignment.personId) {
          cell.people.push({
            personId: assignment.personId,
            positionId: assignment.positionId,
            status: assignment.status,
          })
        }
      }

      /*
       * The songs, keyed by plan — mirroring routes/admin.php's `/matrix`.
       *
       * Both halves have to build the same shape or the preview build quietly
       * disagrees with the real one, which is the kind of divergence that only
       * shows up on a Sunday. The key resolves in the same order as the
       * running order: the item's override, then its arrangement, then the
       * song itself.
       */
      const songTitle = new Map(allSongs.map((s) => [s.id, s.title]))
      const songKey = new Map(allSongs.map((s) => [s.id, s.originalKey]))
      const arrKey = new Map(allArrangements.map((a) => [a.id, a.key]))
      const songs: Record<string, MatrixSong[]> = {}

      for (const item of sortBy(allItems.filter((i) => i.type === 'song' && planIds.has(i.planId)),
                                (i) => `${i.planId} ${String(i.sortOrder).padStart(6, '0')}`)) {
        ;(songs[item.planId] ||= []).push({
          songId: item.songId ?? null,
          title: item.title || (item.songId ? songTitle.get(item.songId) ?? 'Song' : 'Song'),
          key: item.keyOverride
            ?? (item.arrangementId ? arrKey.get(item.arrangementId) : undefined)
            ?? (item.songId ? songKey.get(item.songId) : undefined)
            ?? null,
        })
      }

      return { plans, teams: sortBy(teams, (t) => t.sortOrder), cells, songs }
    },
  },
  {
    method: 'GET',
    pattern: '/blockouts',
    visibility: 'admin',
    description: 'Volunteer unavailability. Filter with ?personId=',
    handler: async (ctx) => {
      const personId = ctx.query.get('personId')
      const blockouts = personId
        ? await repo.byIndex('blockouts', 'by-person', personId)
        : await repo.all('blockouts')
      return sortBy(blockouts, (b) => b.startDate)
    },
  },
  {
    method: 'POST',
    pattern: '/blockouts',
    visibility: 'admin',
    description: 'Logs a date range a volunteer is unavailable.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Blockout>>(ctx.body)
      if (!body.personId || !body.startDate) {
        throw new ApiError(400, 'invalid_body', 'personId and startDate are required')
      }
      const now = ts()
      const blockout: Blockout = {
        id: uid('blk'),
        personId: body.personId,
        startDate: body.startDate,
        endDate: body.endDate || body.startDate,
        reason: body.reason,
        repeatsAnnually: Boolean(body.repeatsAnnually),
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('blockouts', blockout)
      return blockout
    },
  },
  {
    method: 'DELETE',
    pattern: '/blockouts/:blockoutId',
    visibility: 'admin',
    description: 'Removes a blockout.',
    handler: async (ctx) => {
      required(await repo.get('blockouts', ctx.params.blockoutId), 'Blockout')
      await repo.remove('blockouts', ctx.params.blockoutId)
      return { id: ctx.params.blockoutId, deleted: true }
    },
  },
]

/* -------------------------------------------------------------------------- */
/* Songs                                                                      */
/* -------------------------------------------------------------------------- */

const songRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/songs',
    visibility: 'admin',
    description: 'Full song library. Filter with ?q=, ?theme=, ?isPublic=true',
    handler: async (ctx) => {
      const q = ctx.query.get('q')?.toLowerCase().trim()
      const theme = ctx.query.get('theme')
      const isPublic = ctx.query.get('isPublic')
      let songs = await repo.all('songs')
      if (q) {
        songs = songs.filter((s) =>
          `${s.title} ${s.artist} ${s.themes.join(' ')} ${s.lyrics}`.toLowerCase().includes(q),
        )
      }
      if (theme) songs = songs.filter((s) => s.themes.includes(theme))
      if (isPublic) songs = songs.filter((s) => s.isPublic === (isPublic === 'true'))
      return sortBy(songs, (s) => s.title.toLowerCase())
    },
  },
  {
    method: 'GET',
    pattern: '/songs/:songId',
    visibility: 'admin',
    description: 'One song joined with all of its arrangements.',
    handler: async (ctx) => {
      const song = required(await repo.get('songs', ctx.params.songId), 'Song', ctx.params.songId)
      const arrangements = await repo.byIndex('arrangements', 'by-song', song.id)
      return { ...song, arrangements: sortBy(arrangements, (a) => (a.isDefault ? 0 : 1)) }
    },
  },
  {
    method: 'POST',
    pattern: '/songs',
    visibility: 'admin',
    description: 'Adds a song to the library.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Song>>(ctx.body)
      if (!body.title?.trim()) throw new ApiError(400, 'invalid_body', 'title is required')
      const now = ts()
      const song: Song = {
        id: uid('song'),
        title: body.title.trim(),
        artist: body.artist?.trim() || 'Unknown',
        author: body.author,
        ccliNumber: body.ccliNumber,
        copyright: body.copyright,
        bpm: body.bpm ?? 72,
        originalKey: body.originalKey ?? 'C',
        meter: body.meter,
        themes: body.themes ?? [],
        lyrics: body.lyrics ?? '',
        lyricsRich: body.lyricsRich,
        chordSheet: body.chordSheet,
        youtubeUrl: body.youtubeUrl,
        isPublic: Boolean(body.isPublic),
        timesScheduled: 0,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('songs', song)

      /*
       * Every song starts with one arrangement — the key, the tempo and the
       * LENGTH live there, and a song without one lands in a plan at a flat
       * five minutes with an empty Arrangement column. Mirrors the same block
       * in api/routes/library.php.
       */
      await repo.put('arrangements', {
        id: uid('arr'),
        songId: song.id,
        name: 'Original',
        key: song.originalKey,
        bpm: song.bpm,
        sequence: [],
        lengthSeconds: 300,
        isDefault: true,
        createdAt: now,
        updatedAt: now,
      })

      return song
    },
  },
  {
    method: 'PATCH',
    pattern: '/songs/:songId',
    visibility: 'admin',
    description: 'Updates a song — including the "Expose to Public App" flag.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Song>>(ctx.body)
      return required(
        await repo.patch('songs', ctx.params.songId, body),
        'Song',
        ctx.params.songId,
      )
    },
  },
  {
    method: 'DELETE',
    pattern: '/songs/:songId',
    visibility: 'admin',
    description: 'Deletes a song and its arrangements.',
    handler: async (ctx) => {
      const { songId } = ctx.params
      required(await repo.get('songs', songId), 'Song', songId)
      await repo.removeByIndex('arrangements', 'by-song', songId)
      await repo.remove('songs', songId)
      return { id: songId, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/arrangements',
    visibility: 'admin',
    description: 'Arrangements. Filter with ?songId=',
    handler: async (ctx) => {
      const songId = ctx.query.get('songId')
      return songId ? repo.byIndex('arrangements', 'by-song', songId) : repo.all('arrangements')
    },
  },
  {
    method: 'POST',
    pattern: '/arrangements',
    visibility: 'admin',
    description: 'Creates an arrangement for a song.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Arrangement>>(ctx.body)
      if (!body.songId) throw new ApiError(400, 'invalid_body', 'songId is required')
      const song = required(await repo.get('songs', body.songId), 'Song', body.songId)
      const now = ts()
      const arrangement: Arrangement = {
        id: uid('arr'),
        songId: song.id,
        name: body.name?.trim() || 'New arrangement',
        key: body.key ?? song.originalKey,
        bpm: body.bpm ?? song.bpm,
        sequence: body.sequence ?? ['Intro', 'Verse 1', 'Chorus', 'Outro'],
        lengthSeconds: body.lengthSeconds ?? 300,
        notes: body.notes,
        isDefault: Boolean(body.isDefault),
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('arrangements', arrangement)

      /* The first arrangement of a song is its default whether anybody asked or
         not, and a new default clears the others. Mirrors the PHP in
         api/routes/library.php — these two answer the same requests and a
         difference between them is a bug nobody sees until production. */
      const siblings = await repo.byIndex('arrangements', 'by-song', song.id)
      if (arrangement.isDefault || siblings.length === 1) {
        arrangement.isDefault = true
        await repo.put('arrangements', arrangement)
        for (const other of siblings) {
          if (other.id !== arrangement.id && other.isDefault) {
            await repo.put('arrangements', { ...other, isDefault: false })
          }
        }
      }
      return arrangement
    },
  },
  {
    method: 'PATCH',
    pattern: '/arrangements/:arrangementId',
    visibility: 'admin',
    description: 'Updates an arrangement.',
    handler: async (ctx) => {
      const body = requireBody<Partial<Arrangement>>(ctx.body)
      const { arrangementId } = ctx.params
      const existing = required(
        await repo.get('arrangements', arrangementId), 'Arrangement', arrangementId,
      )
      const updated = required(
        await repo.patch('arrangements', arrangementId, body), 'Arrangement', arrangementId,
      )

      if (body.isDefault) {
        const siblings = await repo.byIndex('arrangements', 'by-song', existing.songId)
        for (const other of siblings) {
          if (other.id !== arrangementId && other.isDefault) {
            await repo.put('arrangements', { ...other, isDefault: false })
          }
        }
      }
      return updated
    },
  },
  {
    method: 'DELETE',
    pattern: '/arrangements/:arrangementId',
    visibility: 'admin',
    description: 'Deletes an arrangement.',
    handler: async (ctx) => {
      const { arrangementId } = ctx.params
      const existing = required(
        await repo.get('arrangements', arrangementId), 'Arrangement', arrangementId,
      )
      await repo.remove('arrangements', arrangementId)

      /* If the default went, promote the oldest of what is left rather than
         leaving a song with no default at all. */
      if (existing.isDefault) {
        const left = sortBy(
          await repo.byIndex('arrangements', 'by-song', existing.songId),
          (a) => a.createdAt,
        )
        if (left[0]) await repo.put('arrangements', { ...left[0], isDefault: true })
      }
      return { id: arrangementId, deleted: true }
    },
  },
]

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

const notificationRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/notifications',
    visibility: 'admin',
    description: 'Feed for the bell icon. ?personId= returns personal + broadcast.',
    handler: async (ctx) => {
      const personId = ctx.query.get('personId')
      const unreadOnly = ctx.query.get('unread') === 'true'
      const relatedPlanId = ctx.query.get('relatedPlanId')
      let notifications = await repo.all('notifications')
      if (personId) {
        notifications = notifications.filter((n) => n.personId === personId || n.personId === null)
      }
      if (unreadOnly) notifications = notifications.filter((n) => !n.isRead)
      // Everything already sent about one service — how the Teams tab knows
      // who has been told and who has not.
      if (relatedPlanId) {
        notifications = notifications.filter((n) => n.relatedPlanId === relatedPlanId)
      }
      return notifications.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    },
  },
  {
    method: 'POST',
    pattern: '/notifications',
    visibility: 'admin',
    description: 'Creates a notification (also used by the Admin simulation panel).',
    handler: async (ctx) => {
      const body = requireBody<Partial<AppNotification>>(ctx.body)
      const now = ts()

      /*
       * On an auto-confirm rota there is nothing to accept, so an actionable
       * request would show Accept/Decline buttons that contradict a status
       * already set to confirmed. Downgrade it to a plain announcement —
       * mirrors the same rule in api/routes/library.php.
       */
      let type = body.type ?? 'general_alert'
      if (type === 'scheduling_request' && body.relatedPlanId) {
        const plan = await repo.get('plans', body.relatedPlanId)
        const serviceType = plan ? await repo.get('serviceTypes', plan.serviceTypeId) : undefined
        if (serviceType?.autoConfirm) type = 'plan_update'
      }

      const notification: AppNotification = {
        id: uid('notif'),
        type,
        title: body.title?.trim() || 'Notification',
        body: body.body?.trim() || '',
        priority: body.priority ?? 'normal',
        personId: body.personId ?? null,
        isRead: false,
        actionResolved: type !== 'scheduling_request',
        link: body.link,
        relatedPlanId: body.relatedPlanId,
        relatedAssignmentId: body.relatedAssignmentId,
        relatedSongId: body.relatedSongId,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('notifications', notification)
      return notification
    },
  },
  {
    method: 'PATCH',
    pattern: '/notifications/:notificationId',
    visibility: 'admin',
    description: 'Marks read/unread or records an Accept/Decline action.',
    handler: async (ctx) => {
      const body = requireBody<Partial<AppNotification>>(ctx.body)
      const notification = required(
        await repo.get('notifications', ctx.params.notificationId),
        'Notification',
        ctx.params.notificationId,
      )
      const patch: Partial<AppNotification> = { ...body }

      // Accepting or declining a scheduling request also moves the assignment.
      if (body.actionTaken === 'accepted' || body.actionTaken === 'declined') {
        patch.actionResolved = true
        patch.isRead = true
        if (notification.relatedAssignmentId) {
          await repo.patch('assignments', notification.relatedAssignmentId, {
            status: body.actionTaken === 'accepted' ? 'confirmed' : 'declined',
            respondedAt: ts(),
          })
        }
      }
      return required(
        await repo.patch('notifications', notification.id, patch),
        'Notification',
        notification.id,
      )
    },
  },
  {
    method: 'POST',
    pattern: '/notifications/read-all',
    visibility: 'admin',
    description: 'Marks the whole feed as read. Body: { personId }',
    handler: async (ctx) => {
      const body = requireBody<{ personId?: string | null }>(ctx.body)
      const all = await repo.all('notifications')
      const target = all.filter(
        (n) =>
          !n.isRead &&
          (!body.personId || n.personId === body.personId || n.personId === null),
      )
      const now = ts()
      await repo.putMany(
        'notifications',
        target.map((n) => ({ ...n, isRead: true, updatedAt: now })),
      )
      return { updated: target.length }
    },
  },
  {
    method: 'DELETE',
    pattern: '/notifications/:notificationId',
    visibility: 'admin',
    description: 'Dismisses a notification permanently.',
    handler: async (ctx) => {
      await repo.remove('notifications', ctx.params.notificationId)
      return { id: ctx.params.notificationId, deleted: true }
    },
  },
]

/* -------------------------------------------------------------------------- */
/* Admin: API keys, settings, maintenance                                     */
/* -------------------------------------------------------------------------- */

function generateApiKey(): string {
  const bytes = new Uint8Array(10)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `mdv_live_${hex}`
}

const adminRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/api-keys',
    visibility: 'admin',
    description: 'API keys issued to external apps.',
    handler: async () => sortBy(await repo.all('apiKeys'), (k) => k.createdAt),
  },
  {
    method: 'POST',
    pattern: '/api-keys',
    visibility: 'admin',
    description: 'Generates a new API key. Body: { label, scopes }',
    handler: async (ctx) => {
      const body = requireBody<Partial<ApiKey>>(ctx.body)
      const now = ts()
      const key: ApiKey = {
        id: uid('key'),
        label: body.label?.trim() || 'Untitled key',
        key: generateApiKey(),
        scopes: body.scopes?.length ? body.scopes : ['songs:read'],
        revoked: false,
        createdAt: now,
        updatedAt: now,
      }
      await repo.put('apiKeys', key)
      return key
    },
  },
  {
    method: 'PATCH',
    pattern: '/api-keys/:keyId',
    visibility: 'admin',
    description: 'Revokes or relabels an API key.',
    handler: async (ctx) => {
      const body = requireBody<Partial<ApiKey>>(ctx.body)
      return required(await repo.patch('apiKeys', ctx.params.keyId, body), 'API key')
    },
  },
  {
    method: 'DELETE',
    pattern: '/api-keys/:keyId',
    visibility: 'admin',
    description: 'Deletes an API key.',
    handler: async (ctx) => {
      await repo.remove('apiKeys', ctx.params.keyId)
      return { id: ctx.params.keyId, deleted: true }
    },
  },
  {
    method: 'GET',
    pattern: '/settings',
    visibility: 'admin',
    description: 'Organisation settings and the signed-in person.',
    handler: async () => required(await repo.get('settings', 'app-settings'), 'Settings'),
  },
  {
    method: 'PATCH',
    pattern: '/settings',
    visibility: 'admin',
    description: 'Updates organisation settings.',
    handler: async (ctx) => {
      const body = requireBody<Partial<AppSettings>>(ctx.body)
      return required(await repo.patch('settings', 'app-settings', body), 'Settings')
    },
  },
  {
    method: 'POST',
    pattern: '/maintenance/reseed',
    visibility: 'admin',
    description: 'Wipes local data and re-seeds the demo organisation.',
    handler: async () => {
      await seedDatabase(true)
      return { reseeded: true, at: ts() }
    },
  },
  {
    method: 'GET',
    pattern: '/maintenance/stats',
    visibility: 'admin',
    description: 'Row counts per IndexedDB store, shown in Admin -> Storage.',
    handler: async () => {
      const stores = [
        'ministries', 'teams', 'positions', 'people', 'memberships', 'serviceTypes',
        'plans', 'planItems', 'songs', 'arrangements', 'assignments', 'blockouts',
        'notifications', 'apiKeys', 'syncQueue',
      ] as const
      const counts = await Promise.all(stores.map((s) => repo.count(s)))
      return Object.fromEntries(stores.map((s, i) => [s, counts[i]]))
    },
  },
]

export const administrativeRoutes: Route[] = [
  ...orgRoutes,
  ...planRoutes,
  ...scheduleRoutes,
  ...songRoutes,
  ...notificationRoutes,
  ...adminRoutes,
]
